import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAdminLang } from "./adminLang";
import {
  LayoutDashboard, Users, Inbox, FileText, Briefcase, Handshake, UsersRound,
  HelpCircle, FileDown, MapPin, Settings, ShieldCheck, LogOut, Mail, ScrollText, Newspaper,
  Home, Layers, Target,
} from "lucide-react";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/leads", label: "Leads", icon: Users, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/inbox/coverage", label: "Couverture", icon: Inbox, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/inbox/quotes", label: "Devis", icon: FileText, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/inbox/contact", label: "Messages", icon: Mail, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/inbox/applications", label: "Candidatures", icon: Briefcase, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/inbox/newsletter", label: "Newsletter", icon: Newspaper, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/home", label: "Page d'accueil", icon: Home, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/solutions", label: "Solutions", icon: Layers, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/sectors", label: "Secteurs", icon: Target, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/posts", label: "Actualités", icon: ScrollText, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/jobs", label: "Emplois", icon: Briefcase, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/partners", label: "Partenaires", icon: Handshake, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/team", label: "Équipe", icon: UsersRound, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/faqs", label: "FAQ", icon: HelpCircle, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/documents", label: "Documents", icon: FileDown, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/locations", label: "Bureaux", icon: MapPin, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/content/pages", label: "Pages / SEO", icon: FileText, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/settings", label: "Paramètres", icon: Settings, roles: ["super_admin", "editor", "sales", "analyst"] },
  { to: "/admin/users", label: "Utilisateurs", icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/admin/audit", label: "Audit & envois", icon: ScrollText, roles: ["super_admin", "editor", "sales", "analyst"] },
];

const NAV_EN = {
  "/admin": "Dashboard", "/admin/leads": "Leads", "/admin/inbox/coverage": "Coverage",
  "/admin/inbox/quotes": "Quotes", "/admin/inbox/contact": "Messages", "/admin/inbox/applications": "Applications",
  "/admin/inbox/newsletter": "Newsletter", "/admin/home": "Home page", "/admin/solutions": "Solutions",
  "/admin/sectors": "Sectors", "/admin/content/posts": "News", "/admin/content/jobs": "Jobs",
  "/admin/content/partners": "Partners", "/admin/content/team": "Team", "/admin/content/faqs": "FAQ",
  "/admin/content/documents": "Documents", "/admin/content/locations": "Offices", "/admin/content/pages": "Pages / SEO",
  "/admin/settings": "Settings", "/admin/users": "Users", "/admin/audit": "Audit & emails",
};

export const AdminLayout = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { lang, setLang, tr } = useAdminLang();

  useEffect(() => {
    const token = localStorage.getItem("bt_admin_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("bt_admin_token"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#08090A] flex items-center justify-center"><span className="text-[#FF6600] font-mono text-sm animate-pulse">BULA TELECOMS — CMS</span></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const canWrite = user.role !== "analyst";
  const canLeads = ["super_admin", "sales"].includes(user.role);
  const isSuper = user.role === "super_admin";

  const logout = () => { localStorage.removeItem("bt_admin_token"); navigate("/admin/login"); };

  return (
    <AdminCtx.Provider value={{ user, canWrite, canLeads, isSuper }}>
      <div className="min-h-screen bg-[#08090A] text-white flex" data-testid="admin-shell">
        <aside className="w-60 shrink-0 border-r border-white/10 min-h-screen sticky top-0 h-screen overflow-y-auto hidden md:block">
          <div className="p-5 border-b border-white/10">
            <p className="font-black text-lg leading-none">BULA <span className="text-[#FF6600]">CMS</span></p>
            <p className="mt-1.5 text-[11px] text-white/40 truncate">{user.email}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF9900]">{user.role}</p>
            <div className="mt-3 inline-flex items-center rounded-full border border-white/15 overflow-hidden" role="group" aria-label="Interface language">
              <button data-testid="admin-lang-fr" onClick={() => setLang("fr")}
                className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${lang === "fr" ? "bg-[#FF6600] text-[#08090A]" : "text-white/60 hover:text-white"}`}>FR</button>
              <button data-testid="admin-lang-en" onClick={() => setLang("en")}
                className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${lang === "en" ? "bg-[#FF6600] text-[#08090A]" : "text-white/60 hover:text-white"}`}>EN</button>
            </div>
          </div>
          <nav className="p-3 space-y-0.5" aria-label="Administration">
            {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
              <NavLink key={n.to} end={n.end} to={n.to} data-testid={`admin-nav-${n.to.split("/").pop()}`}
                title={lang === "en" ? (NAV_EN[n.to] || n.label) : n.label}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold whitespace-nowrap transition-colors ${isActive ? "bg-[#FF6600] text-[#08090A]" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                <n.icon size={15} className="shrink-0" /> <span className="truncate">{lang === "en" ? (NAV_EN[n.to] || n.label) : n.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-white/10">
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold text-white/50 hover:text-red-400 hover:bg-white/5 w-full transition-colors">
              <LogOut size={15} /> {tr("Déconnexion", "Sign out")}
            </button>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-4 sm:p-8"><Outlet /></main>
      </div>
    </AdminCtx.Provider>
  );
};
